import React, { useState, useEffect } from 'react';
import { supabase } from '../services/supabaseClient';

// ─── ADMIN VIEW ───────────────────────────────────────────────────────────────
const AdminPayroll = () => {
const [employees, setEmployees] = useState([]);
const [selectedEmployee, setSelectedEmployee] = useState(null);
const [sessions, setSessions] = useState([]);
const [view, setView] = useState('month');
const [loading, setLoading] = useState(true);
const [showAddSession, setShowAddSession] = useState(false);
const [newSession, setNewSession] = useState({
date: new Date().toISOString().split('T')[0],
hours: '',
positiveComment: false,
positiveReview: false,
negativeComment: false,
negativeReview: false,
note: ''
});

const BASE_WAGE = 165;
const POSITIVE_COMMENT_BONUS = 10;
const POSITIVE_REVIEW_BONUS = 10;

useEffect(() => { loadEmployees(); }, []);
useEffect(() => { if (selectedEmployee) loadSessions(selectedEmployee.id); }, [selectedEmployee]);

const loadEmployees = async () => {
setLoading(true);
const { data } = await supabase.from('employees').select('*').order('name');
setEmployees(data || []);
setLoading(false);
};

const loadSessions = async (employeeId) => {
const { data } = await supabase
.from('payroll_sessions')
.select('*')
.eq('employee_id', employeeId)
.order('date', { ascending: false });
setSessions(data || []);
};

const filterSessions = () => {
const now = new Date();
return sessions.filter(s => {
const d = new Date(s.date);
if (view === 'day') return d.toDateString() === now.toDateString();
if (view === 'week') { const w = new Date(now); w.setDate(now.getDate() - 7); return d >= w; }
if (view === 'month') { return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear(); }
return true; // 'all'
});
};

const addSession = async () => {
if (!newSession.date || !newSession.hours || !selectedEmployee) return;
const rate = BASE_WAGE +
(newSession.positiveComment ? POSITIVE_COMMENT_BONUS : 0) +
(newSession.positiveReview ? POSITIVE_REVIEW_BONUS : 0);
const total = rate * parseFloat(newSession.hours);
const { error } = await supabase.from('payroll_sessions').insert([{
employee_id: selectedEmployee.id,
employee_name: selectedEmployee.name,
date: newSession.date,
hours: parseFloat(newSession.hours),
hourly_rate: rate,
total_wage: total,
positive_comment: newSession.positiveComment,
positive_review: newSession.positiveReview,
negative_comment: newSession.negativeComment,
negative_review: newSession.negativeReview,
note: newSession.note
}]);
if (!error) {
await loadSessions(selectedEmployee.id);
setShowAddSession(false);
setNewSession({
date: new Date().toISOString().split('T')[0],
hours: '', positiveComment: false, positiveReview: false,
negativeComment: false, negativeReview: false, note: ''
});
}
};

const deleteSession = async (id) => {
if (!window.confirm('Ta bort detta pass?')) return;
await supabase.from('payroll_sessions').delete().eq('id', id);
await loadSessions(selectedEmployee.id);
};

const filtered = filterSessions();
const totalHours = filtered.reduce((s, x) => s + (x.hours || 0), 0);
const totalWage = filtered.reduce((s, x) => s + (x.total_wage || 0), 0);
const totalPositive = filtered.filter(x => x.positive_comment).length;
const totalNegative = filtered.filter(x => x.negative_comment).length;

const inputStyle = { width: '100%', padding: '10px 14px', borderRadius: '10px', border: '2px solid #e8e4d9', backgroundColor: '#f8f7f4', color: '#1a1a2e', outline: 'none', fontSize: '14px' };
const cardStyle = { backgroundColor: '#fff', border: '1px solid #e8e4d9', borderRadius: '16px', padding: '20px' };

return (
<div style={{ color: '#1a1a2e' }}>
<h2 className="text-2xl font-bold mb-6" style={{ color: '#1a1a2e' }}>👥 Lönehantering</h2>

<div className="grid md:grid-cols-4 gap-4 mb-6">
{loading ? (
<div className="col-span-4 text-center py-8" style={{ color: '#888' }}>Laddar personal...</div>
) : employees.map(emp => (
<button key={emp.id} onClick={() => { setSelectedEmployee(emp); setShowAddSession(false); }}
className="p-4 rounded-2xl text-left transition-all hover:scale-105"
style={{
border: `2px solid ${selectedEmployee?.id === emp.id ? '#C9A84C' : '#e8e4d9'}`,
backgroundColor: selectedEmployee?.id === emp.id ? 'rgba(201,168,76,0.08)' : '#fff'
}}>
<div className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg mb-2"
style={{ backgroundColor: '#1a1a2e', color: '#C9A84C' }}>
{emp.name?.charAt(0).toUpperCase()}
</div>
<p className="font-bold text-sm" style={{ color: '#1a1a2e' }}>{emp.name}</p>
<p className="text-xs" style={{ color: '#888' }}>{emp.email}</p>
</button>
))}
</div>

{selectedEmployee && (
<div>
<div className="flex flex-wrap items-center justify-between gap-4 mb-6">
<div>
<h3 className="text-xl font-bold" style={{ color: '#1a1a2e' }}>{selectedEmployee.name}</h3>
<p className="text-sm" style={{ color: '#888' }}>
Grundlön: {BASE_WAGE} kr/h · +{POSITIVE_COMMENT_BONUS} kr positiv kommentar · +{POSITIVE_REVIEW_BONUS} kr positiv recension
</p>
</div>
<div className="flex gap-2 flex-wrap">
{['day','week','month','all'].map(v => (
<button key={v} onClick={() => setView(v)}
className="px-4 py-2 rounded-full font-semibold text-sm transition-all"
style={{ backgroundColor: view === v ? '#1a1a2e' : '#f8f7f4', color: view === v ? '#C9A84C' : '#555', border: '2px solid #e8e4d9' }}>
{v === 'day' ? 'Dag' : v === 'week' ? 'Vecka' : v === 'month' ? 'Månad' : 'Alla'}
</button>
))}
<button onClick={() => setShowAddSession(!showAddSession)}
className="px-4 py-2 rounded-full font-bold text-sm transition-all"
style={{ backgroundColor: '#C9A84C', color: '#1a1a2e' }}>
+ Lägg till pass
</button>
</div>
</div>

<div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
{[
{ label: 'Timmar', value: `${totalHours.toFixed(1)}h`, color: '#1a1a2e' },
{ label: 'Lön att betala', value: `${Math.round(totalWage).toLocaleString()} kr`, color: '#C9A84C' },
{ label: 'Positiva kommentarer', value: totalPositive, color: '#16a34a' },
{ label: 'Negativa kommentarer', value: totalNegative, color: '#dc2626' }
].map((s, i) => (
<div key={i} style={cardStyle}>
<p className="text-xs mb-1" style={{ color: '#888' }}>{s.label}</p>
<p className="text-2xl font-bold" style={{ color: s.color }}>{s.value}</p>
</div>
))}
</div>

{showAddSession && (
<div className="mb-6 p-6 rounded-2xl" style={{ backgroundColor: '#fff', border: '2px solid #C9A84C' }}>
<h4 className="font-bold mb-4" style={{ color: '#1a1a2e' }}>Nytt pass för {selectedEmployee.name}</h4>
<div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
<div>
<label className="block text-xs font-bold mb-1" style={{ color: '#888' }}>Datum</label>
<input type="date" value={newSession.date}
onChange={e => setNewSession(p => ({ ...p, date: e.target.value }))}
style={inputStyle} />
</div>
<div>
<label className="block text-xs font-bold mb-1" style={{ color: '#888' }}>Antal timmar</label>
<input type="number" step="0.5" value={newSession.hours}
onChange={e => setNewSession(p => ({ ...p, hours: e.target.value }))}
placeholder="T.ex. 3.5" style={inputStyle} />
</div>
</div>

<div className="grid grid-cols-2 gap-3 mb-4">
{[
{ key: 'positiveComment', label: '✅ Positiv kommentar från kund', color: '#dcfce7', border: '#86efac', textColor: '#16a34a' },
{ key: 'positiveReview', label: '⭐ Positiv recension (Google/Trustpilot)', color: '#dcfce7', border: '#86efac', textColor: '#16a34a' },
{ key: 'negativeComment', label: '❌ Negativ kommentar från kund', color: '#fee2e2', border: '#fca5a5', textColor: '#dc2626' },
{ key: 'negativeReview', label: '👎 Negativ recension', color: '#fee2e2', border: '#fca5a5', textColor: '#dc2626' },
].map(cb => (
<label key={cb.key} className="flex items-center gap-2 p-3 rounded-xl cursor-pointer"
style={{ backgroundColor: newSession[cb.key] ? cb.color : '#f8f7f4', border: `1px solid ${newSession[cb.key] ? cb.border : '#e8e4d9'}` }}>
<input type="checkbox" checked={newSession[cb.key]}
onChange={e => setNewSession(p => ({ ...p, [cb.key]: e.target.checked }))}
className="w-4 h-4" />
<span className="text-xs font-semibold" style={{ color: newSession[cb.key] ? cb.textColor : '#555' }}>{cb.label}</span>
</label>
))}
</div>

<div className="p-3 rounded-xl mb-4" style={{ backgroundColor: '#1a1a2e' }}>
<div className="flex justify-between text-sm">
<span style={{ color: 'rgba(255,255,255,0.7)' }}>Timlön för detta pass:</span>
<span className="font-bold" style={{ color: '#C9A84C' }}>
{BASE_WAGE + (newSession.positiveComment ? 10 : 0) + (newSession.positiveReview ? 10 : 0)} kr/h
</span>
</div>
<div className="flex justify-between text-sm mt-1">
<span style={{ color: 'rgba(255,255,255,0.7)' }}>Total lön:</span>
<span className="font-bold text-lg" style={{ color: '#C9A84C' }}>
{Math.round((BASE_WAGE + (newSession.positiveComment ? 10 : 0) + (newSession.positiveReview ? 10 : 0)) * (parseFloat(newSession.hours) || 0)).toLocaleString()} kr
</span>
</div>
</div>

<div className="mb-4">
<label className="block text-xs font-bold mb-1" style={{ color: '#888' }}>Anteckning (valfritt)</label>
<input type="text" value={newSession.note}
onChange={e => setNewSession(p => ({ ...p, note: e.target.value }))}
placeholder="T.ex. Djupstädning Storgatan 5" style={inputStyle} />
</div>

<div className="flex gap-3">
<button onClick={() => setShowAddSession(false)}
className="flex-1 py-3 rounded-full font-bold text-sm"
style={{ backgroundColor: '#f8f7f4', color: '#555', border: '2px solid #e8e4d9' }}>
Avbryt
</button>
<button onClick={addSession}
disabled={!newSession.hours || !newSession.date}
className="flex-1 py-3 rounded-full font-bold text-sm"
style={{ backgroundColor: newSession.hours ? '#1a1a2e' : '#e8e4d9', color: newSession.hours ? '#C9A84C' : '#aaa' }}>
Spara Pass
</button>
</div>
</div>
)}

<div style={cardStyle}>
<h4 className="font-bold mb-4" style={{ color: '#1a1a2e' }}>
Pass —{' '}
{view === 'day' ? 'Idag' : view === 'week' ? 'Senaste 7 dagarna' : view === 'month' ? 'Denna månad' : 'Alla pass'}
<span className="ml-2 text-sm font-normal" style={{ color: '#888' }}>({filtered.length} pass)</span>
</h4>

{filtered.length === 0 ? (
<p className="text-center py-8" style={{ color: '#aaa' }}>Inga pass under denna period</p>
) : (
<div className="overflow-x-auto">
<table className="w-full text-sm">
<thead>
<tr style={{ borderBottom: '2px solid #e8e4d9' }}>
{['Datum','Timmar','Timlön','Total','✅','⭐','❌','👎','Not',''].map((h, i) => (
<th key={i} className="text-left py-2 px-2 font-bold text-xs" style={{ color: '#888' }}>{h}</th>
))}
</tr>
</thead>
<tbody>
{filtered.map(s => (
<tr key={s.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
<td className="py-3 px-2 font-semibold">{s.date}</td>
<td className="py-3 px-2">{s.hours}h</td>
<td className="py-3 px-2 font-bold" style={{ color: '#C9A84C' }}>{s.hourly_rate} kr/h</td>
<td className="py-3 px-2 font-bold">{Math.round(s.total_wage).toLocaleString()} kr</td>
<td className="py-3 px-2 text-center">{s.positive_comment ? '✅' : '—'}</td>
<td className="py-3 px-2 text-center">{s.positive_review ? '⭐' : '—'}</td>
<td className="py-3 px-2 text-center">{s.negative_comment ? '❌' : '—'}</td>
<td className="py-3 px-2 text-center">{s.negative_review ? '👎' : '—'}</td>
<td className="py-3 px-2 text-xs" style={{ color: '#888', maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.note || '—'}</td>
<td className="py-3 px-2">
<button onClick={() => deleteSession(s.id)}
className="text-xs px-2 py-1 rounded-lg"
style={{ backgroundColor: '#fee2e2', color: '#dc2626' }}>
Ta bort
</button>
</td>
</tr>
))}
</tbody>
<tfoot>
<tr style={{ borderTop: '2px solid #e8e4d9', backgroundColor: '#f8f7f4' }}>
<td className="py-3 px-2 font-bold">TOTALT</td>
<td className="py-3 px-2 font-bold">{totalHours.toFixed(1)}h</td>
<td className="py-3 px-2" style={{ color: '#888' }}>—</td>
<td className="py-3 px-2 font-bold text-lg" style={{ color: '#C9A84C' }}>{Math.round(totalWage).toLocaleString()} kr</td>
<td colSpan={6}></td>
</tr>
</tfoot>
</table>
</div>
)}
</div>
</div>
)}

{!selectedEmployee && !loading && (
<div className="text-center py-16 rounded-2xl" style={{ border: '2px dashed #e8e4d9' }}>
<p className="text-lg font-semibold mb-2" style={{ color: '#1a1a2e' }}>Välj en anställd ovan</p>
<p style={{ color: '#888' }}>Klicka på en person för att se och hantera deras löneinfo</p>
</div>
)}
</div>
);
};

// ─── EMPLOYEE VIEW ────────────────────────────────────────────────────────────
const EmployeePayrollView = ({ employee }) => {
const [sessions, setSessions] = useState([]);
const [view, setView] = useState('month');
const [loading, setLoading] = useState(true);

const BASE_WAGE = 165;

useEffect(() => {
const load = async () => {
setLoading(true);
const { data } = await supabase
.from('payroll_sessions')
.select('*')
.eq('employee_id', employee.id)
.order('date', { ascending: false });
setSessions(data || []);
setLoading(false);
};
load();
}, [employee.id]);

const filterSessions = () => {
const now = new Date();
return sessions.filter(s => {
const d = new Date(s.date);
if (view === 'day') return d.toDateString() === now.toDateString();
if (view === 'week') { const w = new Date(now); w.setDate(now.getDate() - 7); return d >= w; }
if (view === 'month') { return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear(); }
return true; // 'all'
});
};

const filtered = filterSessions();
const totalHours = filtered.reduce((s, x) => s + (x.hours || 0), 0);
const totalWage = filtered.reduce((s, x) => s + (x.total_wage || 0), 0);

const cardStyle = { backgroundColor: '#fff', border: '1px solid #e8e4d9', borderRadius: '16px', padding: '20px' };

return (
<div className="max-w-3xl mx-auto px-4 pt-8 pb-20" style={{ color: '#1a1a2e' }}>
<div className="mb-8">
<h1 className="text-2xl font-bold mb-1">Min Lönöversikt 💰</h1>
<p style={{ color: '#888' }}>Grundlön: {BASE_WAGE} kr/h · +10 kr positiv kommentar · +10 kr positiv recension</p>
</div>

<div className="flex gap-2 mb-6 flex-wrap">
{['day','week','month','all'].map(v => (
<button key={v} onClick={() => setView(v)}
className="px-4 py-2 rounded-full font-semibold text-sm"
style={{ backgroundColor: view === v ? '#1a1a2e' : '#f8f7f4', color: view === v ? '#C9A84C' : '#555', border: '2px solid #e8e4d9' }}>
{v === 'day' ? 'Idag' : v === 'week' ? 'Vecka' : v === 'month' ? 'Månad' : 'Alla'}
</button>
))}
</div>

<div className="grid grid-cols-2 gap-4 mb-6">
<div style={cardStyle}>
<p className="text-xs mb-1" style={{ color: '#888' }}>Timmar jobbade</p>
<p className="text-3xl font-bold" style={{ color: '#1a1a2e' }}>{totalHours.toFixed(1)}h</p>
</div>
<div style={cardStyle}>
<p className="text-xs mb-1" style={{ color: '#888' }}>Lön att få</p>
<p className="text-3xl font-bold" style={{ color: '#C9A84C' }}>{Math.round(totalWage).toLocaleString()} kr</p>
</div>
</div>

<div className="mb-6 p-4 rounded-2xl" style={{ backgroundColor: '#1a1a2e' }}>
<h3 className="font-bold mb-3" style={{ color: '#C9A84C' }}>Hur din lön räknas:</h3>
<div className="space-y-2 text-sm">
{[
{ label: 'Grundlön', value: `${BASE_WAGE} kr/h`, color: 'rgba(255,255,255,0.8)' },
{ label: '+ Positiv kommentar från kund', value: '+10 kr/h', color: '#4ade80' },
{ label: '+ Positiv recension (Google/Trustpilot)', value: '+10 kr/h', color: '#4ade80' },
{ label: 'Max timlön', value: '185 kr/h', color: '#C9A84C' },
].map((r, i) => (
<div key={i} className="flex justify-between">
<span style={{ color: 'rgba(255,255,255,0.6)' }}>{r.label}</span>
<span className="font-bold" style={{ color: r.color }}>{r.value}</span>
</div>
))}
</div>
</div>

{loading ? (
<div className="text-center py-8" style={{ color: '#888' }}>Laddar...</div>
) : filtered.length === 0 ? (
<div className="text-center py-12 rounded-2xl" style={cardStyle}>
<p style={{ color: '#888' }}>Inga pass registrerade under denna period</p>
</div>
) : (
<div style={cardStyle}>
<h4 className="font-bold mb-4" style={{ color: '#1a1a2e' }}>
Dina pass —{' '}
{view === 'day' ? 'Idag' : view === 'week' ? 'Senaste veckan' : view === 'month' ? 'Denna månad' : 'Alla pass'}
</h4>
<div className="space-y-3">
{filtered.map(s => (
<div key={s.id} className="p-4 rounded-xl" style={{ backgroundColor: '#f8f7f4', border: '1px solid #e8e4d9' }}>
<div className="flex justify-between items-start mb-2">
<div>
<p className="font-bold" style={{ color: '#1a1a2e' }}>{s.date}</p>
<p className="text-sm" style={{ color: '#888' }}>{s.hours}h · {s.hourly_rate} kr/h</p>
{s.note && <p className="text-xs mt-1" style={{ color: '#aaa' }}>{s.note}</p>}
</div>
<p className="font-bold text-lg" style={{ color: '#C9A84C' }}>{Math.round(s.total_wage).toLocaleString()} kr</p>
</div>
<div className="flex gap-2 flex-wrap">
{s.positive_comment && <span className="text-xs px-2 py-1 rounded-full" style={{ backgroundColor: '#dcfce7', color: '#16a34a' }}>✅ Positiv kommentar</span>}
{s.positive_review && <span className="text-xs px-2 py-1 rounded-full" style={{ backgroundColor: '#dcfce7', color: '#16a34a' }}>⭐ Positiv recension</span>}
{s.negative_comment && <span className="text-xs px-2 py-1 rounded-full" style={{ backgroundColor: '#fee2e2', color: '#dc2626' }}>❌ Negativ kommentar</span>}
{s.negative_review && <span className="text-xs px-2 py-1 rounded-full" style={{ backgroundColor: '#fee2e2', color: '#dc2626' }}>👎 Negativ recension</span>}
</div>
</div>
))}
</div>
<div className="mt-4 p-3 rounded-xl flex justify-between" style={{ backgroundColor: '#1a1a2e' }}>
<span className="font-bold" style={{ color: 'rgba(255,255,255,0.7)' }}>Totalt denna period</span>
<span className="font-bold text-lg" style={{ color: '#C9A84C' }}>{Math.round(totalWage).toLocaleString()} kr</span>
</div>
</div>
)}

<div className="mt-6 p-4 rounded-2xl text-center" style={{ backgroundColor: '#f8f7f4', border: '1px solid #e8e4d9' }}>
<p className="text-sm font-semibold mb-1" style={{ color: '#1a1a2e' }}>Ser något fel?</p>
<p className="text-sm" style={{ color: '#888' }}>Ring Arin direkt på <a href="tel:0739414366" style={{ color: '#C9A84C', fontWeight: 'bold' }}>073-941 43 66</a></p>
</div>
</div>
);
};

// ─── MAIN EXPORT ──────────────────────────────────────────────────────────────
const PayrollDashboard = ({ currentUser }) => {
if (currentUser?.role === 'admin') return <AdminPayroll />;
if (currentUser?.role === 'employee') return <EmployeePayrollView employee={currentUser} />;
return null;
};

export default PayrollDashboard;